import type { FC, ReactNode } from 'react'

import { useEffect } from 'react'
import { gsap, ScrollTrigger } from '@/lib/gsap'
import { Seo } from '@/components/generals/seo'
import { SmoothScroll } from '@/components/generals/smooth-scroll'
import { IntroProvider } from '@/components/landing/intro-context'
import { BookingProvider } from '@/components/landing/booking-provider'
import SkyLayer from '@/components/landing/sky-layer'
import SiteHeader from '@/components/landing/site-header'
import FlyingWordmark from '@/components/landing/flying-wordmark'
import HeroWindow from '@/components/landing/hero-window'
import Ascent from '@/components/landing/ascent'
import CloudRise from '@/components/landing/cloud-rise'
import Fleet from '@/components/landing/fleet'
import Advantages from '@/components/landing/advantages'
import GlobalSection from '@/components/landing/global-section'
import Inquiries from '@/components/landing/inquiries'
import SiteFooter from '@/components/landing/site-footer'
import BookPill from '@/components/landing/book-pill'
import Preloader from '@/components/landing/preloader'

const Home: FC = (): ReactNode => {
    // Page-wide cues, created after every scene so they measure the final layout
    useEffect(() => {
        const root = document.documentElement
        const rise = document.querySelector<HTMLElement>('[data-cloud-rise]')
        const lightZone = document.querySelector<HTMLElement>('[data-light-zone]')
        const globe = document.querySelector<HTMLElement>('#global')
        const sky = document.querySelector<HTMLElement>('[data-sky-root]')
        if (!rise || !lightZone || !globe || !sky) return

        const context = gsap.context(() => {
            // Dark ink in the header while it sits over the ivory scenes
            ScrollTrigger.create({
                trigger: rise,
                start: () => `bottom-=${Math.round(window.innerHeight * 0.2)} bottom`,
                endTrigger: globe,
                end: 'top 40px',
                refreshPriority: -1,
                onToggle: (self) => {
                    root.dataset.headerTheme = self.isActive ? 'light' : 'dark'
                }
            })

            // The fixed sky stops drawing once the ivory scenes fully cover it
            ScrollTrigger.create({
                trigger: lightZone,
                start: 'top top',
                end: 'max',
                refreshPriority: -1,
                toggleClass: { targets: sky, className: 'is-covered' }
            })
        })

        const refresh = (): void => ScrollTrigger.refresh()
        window.addEventListener('load', refresh)

        return () => {
            window.removeEventListener('load', refresh)
            context.revert()
            delete root.dataset.headerTheme
        }
    }, [])

    return (
        <SmoothScroll startStopped>
            <IntroProvider>
                <BookingProvider>
                    <Seo />

                    <SkyLayer />
                    <SiteHeader />
                    <FlyingWordmark />

                    <main className='relative'>
                        <HeroWindow />
                        <Ascent />
                        <CloudRise />

                        <div data-light-zone className='relative z-10 bg-ivory'>
                            <Fleet />
                            <Advantages />
                        </div>

                        <GlobalSection />
                        <Inquiries />
                    </main>

                    <SiteFooter />
                    <BookPill />
                    <Preloader />
                    <div className='film-grain hidden md:block' aria-hidden='true' />
                </BookingProvider>
            </IntroProvider>
        </SmoothScroll>
    )
}

export default Home
